import React, { useMemo, useState } from "react";
import NotificationReportContext from "features/metadesk/Notification/NotificationReport/NotificationReport.context";
import useNotificationReportState from "features/metadesk/Notification/NotificationReport/NotificationReport.hooks";

import NotificationReportContent from "features/metadesk/Notification/NotificationReport/component/NotificationReportContent/NotificationReportContent";
import { Box } from "@mui/material";
import { useIntl } from "react-intl";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/styles";
import { useOutletContext } from "react-router";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import { Tabs } from "@metacrm/metacrm-material-ui/dist/Tabs";
import SmartAlertReportContent from "./component/SmartAlertReportContent/SmartAlertReportContent";

const NotificationReportContainer = () => {
  const {
    notificationList,
    channelPerformance,
    setNotificationList,
    renderContents,
    searchText,
    page,
    dateRange,
    totalItems,
    handleChangePage,
    handleSearchChange,
    setDateRange,
    order,
    orderBy,
    handleRequestSort,
    loading,
    anchorEl,
    actionsList,
    handleCloseActions,
    isFeatureLocked,
  } = useNotificationReportState();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const [entityName] = useOutletContext();
  const { entityConfig } = useFetchEntityConfig();
  const [selectedReport, setSelectedReport] = useState(0);

  const reportTabs = useMemo(() => {
    const tabs = [{ label: formatMessage({ id: "global.category.general" }) }];
    if (entityConfig?.smartNotifiationEnabled) {
      tabs.push({
        label: formatMessage({ id: "global.category.smartNotification" }),
      });
    }
    return tabs;
  }, [entityConfig]);

  const renderReportContents = () => {
    if (selectedReport === 1) {
      return <SmartAlertReportContent />;
    }
    return <NotificationReportContent />;
  };

  return (
    <NotificationReportContext.Provider
      value={{
        notificationList,
        channelPerformance,
        setNotificationList,
        renderContents,
        searchText,
        page,
        dateRange,
        totalItems,
        handleChangePage,
        handleSearchChange,
        setDateRange,
        order,
        orderBy,
        handleRequestSort,
        loading,
        anchorEl,
        actionsList,
        handleCloseActions,
        isFeatureLocked,
      }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        className="fade-up"
      >
        <Box sx={{ width: "100%" }}>
          <Box className="title" mb={3}>
            {formatMessage({ id: "campaignPerformance.header.title" })}
          </Box>
          <Box
            sx={{
              padding: "16px",
              backgroundColor: alpha(theme.customColors.purple[500], 0.15),
              borderRadius: "4px",
              border: `1px solid ${theme.customColors.purple[500]}`,
              display: "flex",
              marginBottom: "24px",
              width: "100%",
            }}
          >
            <Box mr={1}>
              <Box
                component="i"
                className="meta-crm-icon-ic_info_fill font-size-20"
                color={theme.customColors.purple[500]}
              />
            </Box>
            <Box sx={{ color: "#222", lineHeight: "normal" }}>
              <Box mb={0.5} display={"flex"}>
                {formatMessage({
                  id: "notification.report.description",
                })}
              </Box>
              <Box mb={0.5} display={"flex"}>
                <Box mr={0.5}>1.</Box>
                {formatMessage({
                  id: "notification.report.description1",
                })}{" "}
                <Box
                  component="a"
                  href="https://metacrm.gitbook.io/metacrm/settings/general/integration/tracking-tag"
                  target="_blank"
                  className="link"
                  sx={{ textDecoration: "underline", marginLeft: "4px" }}
                >
                  {formatMessage({
                    id: "discordNotificationSettings.webhook.tutorial",
                  })}
                </Box>
              </Box>
              <Box mb={0.5} display={"flex"}>
                <Box mr={0.5}>2.</Box>
                {formatMessage({
                  id: "notification.report.description2",
                })}
                <Box
                  component="a"
                  href={`/${entityName}/analytics/settings/website-traffic`}
                  target="_blank"
                  className="link"
                  sx={{ textDecoration: "underline", marginLeft: "4px" }}
                >
                  {formatMessage({
                    id: "discordNotificationSettings.webhook.tutorial",
                  })}
                </Box>
              </Box>
              <Box display={"flex"}>
                <Box mr={0.5}>3.</Box>
                {formatMessage({
                  id: "notification.report.description3",
                })}
                <Box
                  component="a"
                  href="https://metacrm.gitbook.io/metacrm/products/footprints/website-traffic"
                  target="_blank"
                  className="link"
                  sx={{ textDecoration: "underline", marginLeft: "4px" }}
                >
                  {formatMessage({
                    id: "discordNotificationSettings.webhook.tutorial",
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {reportTabs.length > 1 && (
        <Box mb={2}>
          <Tabs
            value={selectedReport}
            onChange={(e, newValue) => setSelectedReport(newValue)}
            tabs={reportTabs}
          />
        </Box>
      )}
      {renderReportContents()}
    </NotificationReportContext.Provider>
  );
};

export default NotificationReportContainer;
